import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GavelIcon from '@mui/icons-material/Gavel';
import { MED_NECESSITY_CONTRACT_TEXT, CONTRACT_VERSION } from '../../constants/contract';
import api from '../../utils/api';

interface ContractAgreementProps {
  onAccept: () => void;
  onLogout: () => void;
  userEmail?: string;
}

const ContractAgreement: React.FC<ContractAgreementProps> = ({ onAccept, onLogout, userEmail }) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [showScrollAlert, setShowScrollAlert] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const contractParagraphs = useMemo(() => {
    return MED_NECESSITY_CONTRACT_TEXT.split('\n\n');
  }, []);

  const handleScroll = () => {
    if (!contentRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
    const reachedBottom = scrollTop + clientHeight >= scrollHeight - 24;
    if (reachedBottom && !hasScrolledToEnd) {
      setHasScrolledToEnd(true);
      setShowScrollAlert(false);
    }
  };

  const handleAccept = async () => {
    if (!hasScrolledToEnd) {
      setShowScrollAlert(true);
      return;
    }
    const trimmedSignature = signatureName.trim();
    if (!trimmedSignature || trimmedSignature.length < 2) {
      setSubmitError('Please enter your full legal name (at least 2 characters).');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await api.post('/api/contract/sign', {
        signatureName: trimmedSignature,
        contractVersion: CONTRACT_VERSION,
      });

      if (response.data.success) {
        console.log('✅ Contract signed successfully:', response.data.contractSignature);
        // Call parent handler to refresh auth status
        onAccept();
      } else {
        setSubmitError(response.data.error || 'Failed to sign contract. Please try again.');
        setIsSubmitting(false);
      }
    } catch (err: any) {
      console.error('❌ Error signing contract:', err);

      if (err.response?.status === 409) {
        // Already signed
        setSubmitError('You have already signed this contract. You should be redirected shortly...');
        setTimeout(() => {
          onAccept();
        }, 2000);
      } else {
        setSubmitError(err.response?.data?.error || 'An error occurred while signing. Please try again.');
        setIsSubmitting(false);
      }
    }
  };

  const readyToAccept = hasScrolledToEnd && acknowledged && signatureName.trim().length > 1;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: '#f1f5f9',
        py: { xs: 4, md: 6 },
        px: { xs: 2, md: 4 },
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: 'min(1100px, 100%)',
          maxHeight: { xs: '95vh', md: '90vh' },
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: { xs: 3, md: 4 },
            py: { xs: 2.5, md: 3 },
            bgcolor: '#5A8A7F',
            color: '#fff',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <GavelIcon sx={{ fontSize: 32, color: '#ffffff' }} />
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Med Necessity AI Terms & Conditions
                </Typography>
              </Stack>
              <Typography variant="subtitle2" sx={{ mt: 1, opacity: 0.9, color: 'rgba(255,255,255,0.85)' }}>
                All users must sign the latest Terms & Conditions before proceeding.
              </Typography>
            </Box>
            {userEmail && (
              <Box
                sx={{
                  px: 2.5,
                  py: 1.25,
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.4)',
                  bgcolor: 'rgba(255,255,255,0.15)',
                }}
              >
                <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, color: '#fff' }}>
                  Signed in as
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff' }}>
                  {userEmail}
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        {showScrollAlert && !hasScrolledToEnd && (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            Please review the entire agreement by scrolling to the bottom before signing.
          </Alert>
        )}

        {submitError && (
          <Alert severity="error" sx={{ borderRadius: 0 }} onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}

        <Box
          ref={contentRef}
          onScroll={handleScroll}
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: { xs: 3, md: 4 },
            py: { xs: 3, md: 4 },
            bgcolor: '#ffffff',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { background: '#F1F5F3' },
            '&::-webkit-scrollbar-thumb': {
              background: '#5A8A7F',
              borderRadius: 3,
            },
            '&::-webkit-scrollbar-thumb:hover': { background: '#4A7A6F' },
          }}
        >
          {contractParagraphs.map((paragraph, index) => (
            <Typography
              key={index}
              component="p"
              sx={{
                whiteSpace: 'pre-line',
                color: '#1A1A1A',
                fontSize: 14,
                lineHeight: 1.7,
                mb: index === contractParagraphs.length - 1 ? 0 : 2.5,
              }}
            >
              {paragraph}
            </Typography>
          ))}
        </Box>

        <Divider sx={{ borderColor: '#DEE2E6' }} />

        <Box sx={{ px: { xs: 3, md: 4 }, py: { xs: 3, md: 4 }, bgcolor: '#F8F9FA' }}>
          <Stack spacing={2.5}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                  sx={{ '&.Mui-checked': { color: '#5A8A7F' } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: '#1A1A1A' }}>
                  I acknowledge that I have read, understand, and agree to the Med Necessity Terms & Conditions.
                </Typography>
              }
            />

            <TextField
              label="Type your full legal name"
              placeholder="Full name"
              value={signatureName}
              onChange={(event) => setSignatureName(event.target.value)}
              fullWidth
              required
              helperText="Typing your name constitutes your electronic signature."
              FormHelperTextProps={{
                sx: {
                  color: '#495057',
                  fontWeight: 500,
                },
              }}
              inputProps={{ maxLength: 120 }}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ color: hasScrolledToEnd ? '#5A8A7F' : '#868E96', fontSize: 13 }}>
                <CheckCircleOutlineIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'inherit' }}>
                  {hasScrolledToEnd ? 'Contract reviewed in full.' : 'Scroll to the bottom to confirm you have reviewed the entire agreement.'}
                </Typography>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} width={{ xs: '100%', sm: 'auto' }}>
                <Button
                  onClick={onLogout}
                  variant="outlined"
                  color="inherit"
                  startIcon={<LogoutIcon />}
                  sx={{
                    borderColor: '#DEE2E6',
                    color: '#495057',
                    '&:hover': {
                      borderColor: '#5A8A7F',
                      backgroundColor: 'rgba(90, 138, 127, 0.08)',
                    },
                  }}
                >
                  Sign out
                </Button>

                <Button
                  onClick={handleAccept}
                  variant="contained"
                  disableElevation
                  disabled={!readyToAccept || isSubmitting}
                  sx={{
                    minWidth: 180,
                    bgcolor: '#5A8A7F',
                    color: '#ffffff',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: '#4A7A6F',
                    },
                    '&.Mui-disabled': {
                      bgcolor: '#E9ECEF',
                      color: '#868E96',
                    },
                  }}
                  startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
                >
                  {isSubmitting ? 'Signing...' : 'I Agree & Sign'}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default ContractAgreement;

